import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";

type DropdownContextValue = {
  open: boolean;
  setOpen: (v: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  menuId: string;
};

const DropdownContext = createContext<DropdownContextValue | null>(null);

function useDropdown() {
  const ctx = useContext(DropdownContext);
  if (!ctx) throw new Error("DropdownMenu components must be used within DropdownMenu");
  return ctx;
}

export function DropdownMenu({
  children,
  align = "left",
}: {
  children: ReactNode;
  align?: "left" | "right";
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <DropdownContext.Provider value={{ open, setOpen, triggerRef, menuId }}>
      <div className="relative inline-flex" data-dropdown-align={align}>
        {children}
      </div>
    </DropdownContext.Provider>
  );
}

export function DropdownMenuTrigger({
  children,
  className,
  title,
}: {
  children: ReactNode;
  className?: string;
  title?: string;
}) {
  const { open, setOpen, triggerRef, menuId } = useDropdown();

  return (
    <button
      ref={triggerRef as React.RefObject<HTMLButtonElement>}
      type="button"
      title={title}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-controls={open ? menuId : undefined}
      className={clsx(
        "inline-flex items-center gap-1 rounded border border-transparent px-2 py-0.5 text-xs transition hover:border-border hover:bg-panel hover:text-neutral-200",
        open && "border-border bg-panel text-neutral-100",
        className,
      )}
      onClick={() => setOpen(!open)}
    >
      {children}
    </button>
  );
}

export function DropdownMenuContent({
  children,
  className,
  minWidth = 200,
}: {
  children: ReactNode;
  className?: string;
  minWidth?: number;
}) {
  const { open, setOpen, triggerRef, menuId } = useDropdown();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const r = trigger.getBoundingClientRect();
    const align = trigger.closest("[data-dropdown-align]")?.getAttribute("data-dropdown-align") ?? "left";
    setPos({
      top: r.bottom + 4,
      left: align === "right" ? r.right : r.left,
    });
  }, [triggerRef]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, setOpen, triggerRef]);

  useEffect(() => {
    if (!open) return;
    const first = panelRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
    first?.focus();
  }, [open]);

  if (!open || !pos) return null;

  const align = triggerRef.current?.closest("[data-dropdown-align]")?.getAttribute("data-dropdown-align") ?? "left";

  return createPortal(
    <div
      ref={panelRef}
      id={menuId}
      role="menu"
      className={clsx(
        "fixed z-[70] rounded border border-border bg-panel py-1 shadow-xl",
        className,
      )}
      style={{
        top: pos.top,
        left: align === "right" ? undefined : pos.left,
        right: align === "right" ? window.innerWidth - pos.left : undefined,
        minWidth,
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

export function DropdownMenuLabel({ children }: { children: ReactNode }) {
  return (
    <div className="px-3 py-1 text-[10px] uppercase tracking-wide text-muted">{children}</div>
  );
}

export function DropdownMenuSeparator() {
  return <div role="separator" className="my-1 border-t border-border" />;
}

export function DropdownMenuItem({
  children,
  onSelect,
  active,
  disabled,
  className,
}: {
  children: ReactNode;
  onSelect?: () => void;
  active?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  const { setOpen } = useDropdown();

  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      className={clsx(
        "block w-full px-3 py-1.5 text-left text-xs transition hover:bg-accent/40 disabled:cursor-not-allowed disabled:opacity-50",
        active && "bg-accent/30 text-neutral-100",
        className,
      )}
      onClick={() => {
        if (disabled) return;
        onSelect?.();
        setOpen(false);
      }}
    >
      {children}
    </button>
  );
}

export function DropdownMenuLinkItem({
  children,
  onNavigate,
  active,
  description,
}: {
  children: ReactNode;
  onNavigate: () => void;
  active?: boolean;
  description?: string;
}) {
  return (
    <DropdownMenuItem onSelect={onNavigate} active={active}>
      <span className="font-medium">{children}</span>
      {description ? <span className="mt-0.5 block text-[10px] text-muted">{description}</span> : null}
    </DropdownMenuItem>
  );
}
